// Pure calculator logic. No React, no DOM. Tested in isolation.

const OPERATORS = { '+': 1, '-': 1, '*': 2, '/': 2 };

function tokenize(expr) {
  const tokens = [];
  let i = 0;
  while (i < expr.length) {
    const c = expr[i];
    if (c === ' ') { i++; continue; }
    if ((c >= '0' && c <= '9') || c === '.') {
      let num = c; i++;
      while (i < expr.length && ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')) { num += expr[i]; i++; }
      tokens.push({ type: 'num', value: num });
      continue;
    }
    if (c === '+' || c === '-' || c === '*' || c === '/') {
      // Unary minus: at start or after another operator or '('
      const prev = tokens[tokens.length - 1];
      if (c === '-' && (!prev || prev.type === 'op' || prev.value === '(')) {
        // Look ahead: if followed by a digit or '.', fold into a negative number literal
        let j = i + 1;
        while (j < expr.length && expr[j] === ' ') j++;
        if (j < expr.length && ((expr[j] >= '0' && expr[j] <= '9') || expr[j] === '.')) {
          let num = '-';
          i = j;
          while (i < expr.length && ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')) { num += expr[i]; i++; }
          tokens.push({ type: 'num', value: num });
        } else {
          // Fallback: desugar to -1 * (e.g. before '(')
          tokens.push({ type: 'num', value: '-1' });
          tokens.push({ type: 'op', value: '*' });
          i++;
        }
      } else {
        tokens.push({ type: 'op', value: c });
        i++;
      }
      continue;
    }
    if (c === '(' || c === ')') { tokens.push({ type: 'paren', value: c }); i++; continue; }
    return null; // invalid char
  }
  return tokens;
}

function toRPN(tokens) {
  const out = [];
  const stack = [];
  for (const t of tokens) {
    if (t.type === 'num') {
      const n = Number(t.value);
      if (!Number.isFinite(n)) return null;
      out.push(n);
    } else if (t.type === 'op') {
      while (stack.length && stack[stack.length - 1] !== '(' &&
             OPERATORS[stack[stack.length - 1]] >= OPERATORS[t.value]) {
        out.push(stack.pop());
      }
      stack.push(t.value);
    } else if (t.value === '(') {
      stack.push('(');
    } else { // ')'
      while (stack.length && stack[stack.length - 1] !== '(') out.push(stack.pop());
      if (stack.pop() !== '(') return null; // mismatched
    }
  }
  while (stack.length) {
    const op = stack.pop();
    if (op === '(') return null; // mismatched
    out.push(op);
  }
  return out;
}

export function evaluate(expr) {
  if (!expr || !expr.trim()) return 'Error';
  const tokens = tokenize(expr);
  if (!tokens) return 'Error';
  const rpn = toRPN(tokens);
  if (!rpn) return 'Error';
  const st = [];
  for (const t of rpn) {
    if (typeof t === 'number') { st.push(t); continue; }
    const b = st.pop(); const a = st.pop();
    if (a === undefined || b === undefined) return 'Error';
    let r;
    if (t === '+') r = a + b;
    else if (t === '-') r = a - b;
    else if (t === '*') r = a * b;
    else if (t === '/') { if (b === 0) return 'Error'; r = a / b; }
    if (!Number.isFinite(r)) return 'Error';
    st.push(r);
  }
  if (st.length !== 1) return 'Error';
  // Round to avoid floating artefacts, then drop trailing zeros
  return Number(st[0].toFixed(10));
}

function trailingNumberBounds(expr) {
  let start = expr.length;
  while (start > 0 && /[0-9.]/.test(expr[start - 1])) start--;
  // include a leading unary minus that belongs to the number
  if (start > 0 && expr[start - 1] === '-' &&
      (start === 1 || /[+\-*/(]/.test(expr[start - 2]))) start--;
  return { start, num: expr.slice(start) };
}

export function appendToken(expr, token) {
  const isOp = ['+', '-', '*', '/'].includes(token);
  const last = expr[expr.length - 1] || '';
  if (isOp) {
    if (expr === '' && token !== '-') return expr; // no leading binary op except minus
    if (['+', '-', '*', '/'].includes(last)) {
      // Allow '-' after '*', '/', '+' to start a negative operand
      if (token === '-' && ['*', '/', '+'].includes(last)) return expr + token;
      return expr.slice(0, -1) + token;
    }
    return expr + token;
  }
  if (token === '.') {
    const { num } = trailingNumberBounds(expr);
    if (num.includes('.')) return expr; // already has a dot
    if (num === '' ) return expr + '0.';
    return expr + '.';
  }
  // digit
  if (expr === '0') return token; // replace leading zero
  const { start, num } = trailingNumberBounds(expr);
  if (num === '0') return expr.slice(0, start) + token; // replace lone zero token
  return expr + token;
}

export function applyUnary(expr, op) {
  const { start, num } = trailingNumberBounds(expr);
  if (num === '' || num === '-') return expr;
  const n = Number(num);
  if (!Number.isFinite(n)) return expr;
  let r;
  if (op === 'sqrt') r = Math.sqrt(n);
  else if (op === 'square') r = n * n;
  else if (op === 'percent') r = n / 100;
  else if (op === 'negate') {
    if (num.startsWith('-')) return expr.slice(0, start) + num.slice(1);
    return expr.slice(0, start) + '-' + num;
  }
  if (r === undefined || !Number.isFinite(r)) return expr;
  return expr.slice(0, start) + String(Number(r.toFixed(10)));
}

export function backspace(expr) {
  return expr.slice(0, -1);
}
